<template>
  <div>
    <layout-apply-to-steps-popup
      v-if="showApplyToStepsPopup"
      :steps="vformConfig.steps"
      :layout="currentlySelectedLayout"
      :pre-selected-step-uuid="activeStepObject.uuid"
      @apply="applyLayoutToSteps"
      @close="showApplyToStepsPopup = false"
    />

    <div>
      <label for="layout-selector-trigger">Choose Layout:</label>
      <div class="d-flex align-items-center" style="gap: 10px;">
        <layout-miniature
          :layout="currentlySelectedLayout"
          :active="true"
          style="flex-shrink: 0;"
        />
      <div ref="layoutDropdown" class="layout-select-wrapper">
        <div
          id="layout-selector-trigger"
          type="button"
          class="form-text-dark layout-select-trigger"
          @click="toggleLayoutMenu"
        >
          <span>{{ selectedLayout }}</span>
          <span class="layout-select-caret"><icon type="angle-down" /></span>
        </div>

        <div v-if="isLayoutMenuOpen" class="layout-select-menu">
          <div
            v-for="layout in layouts"
            :key="layout.name"
            type="button"
            class="layout-option"
            :class="{ active: layout.name === selectedLayout }"
            @click="selectLayout(layout.name)"
          >
            <layout-miniature :layout="layout" :active="layout.name === selectedLayout" />
            <span class="layout-option-label">{{ layout.name }}</span>
          </div>
        </div>
      </div>
      </div>

      <div class="mt-2 d-flex align-items-center" style="gap: 8px;">
        <div @click="applyLayout()" class="settings-button d-inline-block layout-applier">
          Apply Layout
        </div>
        <div @click="showApplyToStepsPopup = true" class="settings-button d-inline-block layout-applier">
          Apply to Multiple Steps
        </div>
      </div>
    </div>

    <div v-if="activeStepObjectLayout" class="mt-2">
      <h4>Configure Layout Positions</h4>
      <div v-for="position in activeStepObjectLayout.positions" :key="activeStepObject.uuid + position.name">
        <checkbox-widget
            class="mt-2 hotspot-checkbox-for-label"
            :key="activeStepObject.uuid + position.name + position.enabled"
            :initial-value="position.enabled"
            :label-text="$t('vform.' + position.name)"
            :label-for="position.name"
            :disabled="!!position.panelId"
            @change="(val) => {toggleEnabling(position.name, val)}"
        />
      </div>
    </div>
  </div>
</template>

<script>
import {Layouts} from "@/enum";
import CheckboxWidget from "@/components/widgets/editors/Checkbox.vue";
import Icon from "@/components/Icon.vue";
import LayoutMiniature from "@/components/vForm/layout/LayoutMiniature.vue";
import LayoutApplyToStepsPopup from "@/components/vForm/layout/LayoutApplyToStepsPopup.vue";

export default {
  name: "LayoutSelector",
  components: { CheckboxWidget, Icon, LayoutMiniature, LayoutApplyToStepsPopup },
  props: {
    vformConfig: {type: Object, required: true},
    activeStepObject: {type: Object, required: true},
  },
  data() {
    return {
      layouts: Layouts,
      selectedLayout: "none",
      isLayoutMenuOpen: false,
      showApplyToStepsPopup: false,
    };
  },
  computed: {
    currentlySelectedLayout() {
      return this.layouts.find((layout) => layout.name === this.selectedLayout) || this.layouts[0];
    },
    activeStepObjectLayout() {
      return this.activeStepObject.layout;
    }
  },
  watch: {
    activeStepObject() {
      this.setLayoutFromStep();
    },
    'activeStepObject.layout': {
      deep: true,
      handler() {
        this.setLayoutFromStep();
      }
    },
  },
  mounted() {
    this.setLayoutFromStep();
    document.addEventListener("click", this.handleOutsideClick);
  },
  beforeDestroy() {
    document.removeEventListener("click", this.handleOutsideClick);
  },
  methods: {
    setLayoutFromStep() {
      if (this.activeStepObject.layout && this.activeStepObject.layout.name) {
        this.selectedLayout = this.activeStepObject.layout.name;
      } else {
        this.selectedLayout = 'none';
      }
    },
    toggleLayoutMenu() {
      this.isLayoutMenuOpen = !this.isLayoutMenuOpen;
    },
    selectLayout(layoutName) {
      this.selectedLayout = layoutName;
      this.isLayoutMenuOpen = false;
      this.applyLayout();
    },
    handleOutsideClick(event) {
      if (this.$refs.layoutDropdown && !this.$refs.layoutDropdown.contains(event.target)) {
        this.isLayoutMenuOpen = false;
      }
    },
    getPositionConstant(positionName) {
      const positions = this.currentlySelectedLayout.positions;
      const values = Object.values(positions);
      const index = values.indexOf(positionName);
      const keys = Object.keys(positions);
      return keys[index];
    },
    toggleEnabling(position, bool) {
      const posConstant = this.getPositionConstant(position);
      if(this.activeStepObject.layout && this.activeStepObject.layout.positions[posConstant]) {
        const stepIndex = this.vformConfig.steps.findIndex(step => step.uuid === this.activeStepObject.uuid);
        this.$set(this.vformConfig.steps[stepIndex].layout.positions[posConstant], 'enabled', bool);
      }
    },
    // Build a fresh layout object for the currently selected layout,
    // transferring existing position assignments from a given step where possible.
    // When sourceLayout is provided, its enabled/disabled state is used for each position
    // instead of always defaulting to enabled – this preserves custom layout edits when
    // applying to multiple steps (SFX-4675).
    buildLayoutForStep(stepObject, sourceLayout = null) {
      const {positions} = this.currentlySelectedLayout;
      const keys = positions ? Object.keys(positions) : [];
      const layoutObject = { layout: { name: this.selectedLayout, positions: {} } };

      if (!positions) {
        return layoutObject;
      }

      // Transfer positions that exist in both the old and new layout
      if (stepObject.layout && stepObject.layout.positions) {
        const existingKeys = Object.keys(stepObject.layout.positions);
        for (const key of existingKeys) {
          if (keys.includes(key)) {
            layoutObject.layout.positions[key] = { ...stepObject.layout.positions[key] };
          }
        }
      }

      for (const key of keys) {
        // Determine the enabled state: honour sourceLayout when provided so that
        // disabled positions are preserved when applying to multiple steps.
        const enabledFromSource =
          sourceLayout && sourceLayout.positions && sourceLayout.positions[key] !== undefined
            ? sourceLayout.positions[key].enabled
            : true;

        if (!layoutObject.layout.positions[key]) {
          layoutObject.layout.positions[key] = {
            enabled: enabledFromSource,
            panelId: null,
            name: positions[key],
          };
        } else if (sourceLayout) {
          // Position was transferred from the target step; apply enabled state from source.
          layoutObject.layout.positions[key].enabled = enabledFromSource;
        } else {
          // No source layout provided – preserve original behaviour (reset to enabled).
          layoutObject.layout.positions[key].enabled = true;
        }
      }

      return layoutObject;
    },
    applyLayout() {
      const {positions} = this.currentlySelectedLayout;
      if (!positions) {
        this.$set(this.activeStepObject, 'layout', { name: this.selectedLayout, positions: {} });
        return;
      }
      const { layout } = this.buildLayoutForStep(this.activeStepObject);
      this.$set(this.activeStepObject, 'layout', layout);
    },
    applyLayoutToSteps(stepUuids) {
      const sourceLayout = this.activeStepObject.layout;
      for (const uuid of stepUuids) {
        const stepIndex = this.vformConfig.steps.findIndex((s) => s.uuid === uuid);
        if (stepIndex === -1) continue;
        const step = this.vformConfig.steps[stepIndex];
        const { layout } = this.buildLayoutForStep(step, sourceLayout);
        this.$set(this.vformConfig.steps[stepIndex], 'layout', layout);
      }
      this.showApplyToStepsPopup = false;
    },
  },
}
</script>

<style lang="scss" scoped>
#layout-selector-trigger {
  border: 1px solid rgba(255,255,255,0.2);
}
.layout-applier {
  z-index: 0 !important;
  position: relative;
}
.layout-select-wrapper {
  position: relative;
  width: 100%;
  max-width: 340px;
}

.layout-select-trigger {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  text-transform: none;
}

.layout-select-caret {
  opacity: 0.8;
}

.layout-select-menu {
  position: absolute;
  z-index: 20;
  width: 100%;
  margin-top: 4px;
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 4px;
  background: #1c1d21;
  box-shadow: 0 8px 18px rgba(0, 0, 0, 0.35);
  max-height: 260px;
  overflow-y: auto;
}

.layout-option {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 10px;
  border: 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  background: transparent;
  color: #d7d9de;
  text-align: left;
}

.layout-option:last-child {
  border-bottom: 0;
}

.layout-option:hover,
.layout-option.active {
  background: rgba(90, 140, 255, 0.18);
}

.layout-option-label {
  font-size: 0.9rem;
}

</style>